@let hasProjectReadPermission =
  can(RoleAction.Read, RolePermissionSubject.Project);
@let project = hasProjectReadPermission ? (project$ | async) : null;
@let uniqueProfessionIds = uniqueProfessionIds$ | async;
@let agreements = this.agreements$ | async;

@if (shift) {
  <div class="d-flex align-items-center justify-content-between">
    <div class="tooltip-header p-1">
      {{ shift.name }}
    </div>
    <nz-tag [nzColor]="shiftCategoriesColorCode[shift.shiftCategoryId]">{{
      'shift_category.' + shift.shiftCategoryId | translate
    }}</nz-tag>
  </div>
  <nz-divider class="mt-1 mb-1"></nz-divider>
  <div class="d-flex">
    <div class="left-content p-1">
      <wilson-tooltip-shift-schedule-details
        data-test-id="wilson-timeline-shift-tooltip:start-detail"
        [headerText]="'general.start'"
        [plannedDateTime]="plannedShiftStartDateTime"
        [accurateDateTime]="accurateShiftStartDateTime"
        [plannedLocation]="plannedStartLocation"
        [reportedLocation]="reportedStartLocation"
        [timeType]="shiftStartTimeType"
        [timeDifference]="shiftStartTimeDifference"
      ></wilson-tooltip-shift-schedule-details>
      <wilson-tooltip-shift-schedule-details
        data-test-id="wilson-timeline-shift-tooltip:end-detail"
        [headerText]="'general.end'"
        [plannedDateTime]="plannedShiftEndDateTime"
        [accurateDateTime]="accurateShiftEndDateTime"
        [plannedLocation]="plannedEndLocation"
        [reportedLocation]="reportedEndLocation"
        [timeType]="shiftEndTimeType"
        [timeDifference]="shiftEndTimeDifference"
      ></wilson-tooltip-shift-schedule-details>
      @if (viaLocations.length) {
        <div class="mb-2">
          <div class="locations">
            <div class="title">{{ 'page.shifts.locations' | translate }}</div>
            <div class="value">
              {{ viaLocations }}
            </div>
          </div>
        </div>
      }
      @if (project || uniqueProfessionIds?.length) {
        <nz-divider class="mt-1 mb-1 divider"></nz-divider>
      }
      @if (project) {
        <div class="mb-1 project-container">
          <div class="project-info">
            <div class="title">
              {{ ('page.projects.project_details.title' | translate) + ': ' }}
            </div>
            <div class="project-value">
              {{ project.projectId }}
              @if (project.shortName) {
                - {{ project.shortName }}
              }
            </div>
          </div>
          <wilson-project-status-tag
            [status]="project.status"
          ></wilson-project-status-tag>
        </div>
      }
      @if (uniqueProfessionIds?.length) {
        <div class="mb-1 d-flex align-items-center">
          <div class="title me-1">
            {{ ('general.profession' | translate) + ': ' }}
          </div>
          <div class="value">
            @for (professionId of uniqueProfessionIds; track $index) {
              <span>
                {{
                  ('profession.' + professionId | translate) +
                    (!$last ? ', ' : '')
                }}
              </span>
            }
          </div>
        </div>
      }
      @if (('portal-labels' | featureFlagPure | async) && shift.labels) {
        <div class="mb-1">
          <wilson-display-labels
            [selectedLabels]="shift.labels || []"
            [maxAmount]="3"
          ></wilson-display-labels>
        </div>
      }
      <div class="mb-1">
        <div class="title me-1">
          {{
            ('page.shift_timeline.shift.planning_comment' | translate) + ': '
          }}
        </div>
        @let shiftPlanningComment = shiftPlanningCommentSignal();
        @if (shiftPlanningComment) {
          <div
            class="comment-value"
            nz-tooltip
            [nzTooltipTitle]="shiftPlanningCommentTooltip"
            [nzTooltipOverlayStyle]="{ 'max-width': '400px' }"
            [innerHTML]="shiftPlanningComment"
          ></div>
          <ng-template #shiftPlanningCommentTooltip>
            <div [innerHTML]="shiftPlanningComment"></div>
          </ng-template>
        } @else {
          <div class="no-value">{{ 'general.n/a' | translate }}</div>
        }
      </div>
      <div class="mb-1">
        <div class="title me-1">
          {{ ('general.comment' | translate) + ': ' }}
        </div>
        @if (shiftCommentSignal()) {
          <div
            nz-tooltip
            [nzTooltipTitle]="shiftCommentTooltip"
            [nzTooltipOverlayStyle]="{ 'max-width': '400px' }"
            [innerHTML]="shiftCommentSignal()"
            class="comment-value"
          ></div>
          <ng-template #shiftCommentTooltip>
            <div [innerHTML]="shiftCommentSignal()"></div>
          </ng-template>
        } @else {
          <div class="no-value">{{ 'general.n/a' | translate }}</div>
        }
      </div>

      @if (agreements) {
        @let lineBreak = '\n';
        <div
          class="agreement-value"
          nz-tooltip
          [nzTooltipTitle]="agreementTooltip"
          [nzTooltipOverlayStyle]="{ 'max-width': '400px' }"
        >
          <ng-container *ngTemplateOutlet="agreementView"></ng-container>
        </div>

        <ng-template #agreementTooltip>
          <div class="agreements-text">
            {{ agreements | agreementsToText: lineBreak }}
          </div>
        </ng-template>

        <ng-template #agreementView>
          <div class="title">
            {{ ('activity.agreement' | translate) + ': ' }}
          </div>
          <ng-container *ngTemplateOutlet="agreementContent"></ng-container>
        </ng-template>

        <ng-template #agreementContent>
          <wilson-agreement-client-cost-center-name-renderer
            [agreementName]="agreements[0].name"
            [agreementClientName]="
              $safeNavigationMigration(agreements[0].client?.name)
            "
            [costCenter]="agreements[0].costCenter"
            [agreementsLength]="agreements.length"
          ></wilson-agreement-client-cost-center-name-renderer>
        </ng-template>
      }
    </div>
    <div class="right-content p-2">
      <wilson-shift-status-tags
        class="mt-1"
        [shift]="shift"
      ></wilson-shift-status-tags>
      <wilson-priority-tag
        [isEditable]="false"
        [showNA]="true"
        [serviceOrShift]="shift"
        [title]="'general.priority' | translate"
      ></wilson-priority-tag>
      @if (renderingLoadingOrValidations) {
        <div class="content-wrapper">
          <ng-content></ng-content>
        </div>
      }
    </div>
  </div>
}
